import { useState } from 'react'
import { cn } from './internal/utils'

const TYPE_CLASSES = {
  secondary: 'text-[var(--text-muted)]',
  success: 'text-[var(--success-green)]',
  warning: 'text-[var(--warning-color)]',
  danger: 'text-[var(--error-color)]',
}

const LEVEL_CLASSES = {
  1: 'text-[1.75rem] leading-tight font-semibold',
  2: 'text-[1.5rem] leading-tight font-semibold',
  3: 'text-[1.25rem] leading-snug font-semibold',
  4: 'text-[1.125rem] leading-snug font-semibold',
  5: 'text-[1rem] leading-snug font-semibold',
}

const sharedClasses = ({ type, disabled, strong, italic, underline, delete: del, mark, code }) =>
  cn(
    type && TYPE_CLASSES[type],
    disabled && 'cursor-not-allowed text-[var(--gray-500)]',
    strong && 'font-semibold',
    italic && 'italic',
    underline && 'underline',
    del && 'line-through',
    mark && 'bg-[var(--warning-bg)] px-0.5',
    code &&
      'rounded-[var(--radius-xs)] border border-[var(--border-color)] bg-[var(--gray-100)] px-1 font-mono text-[0.85em]'
  )

/** antd `ellipsis` accepts `true` or `{ rows, expandable, tooltip, symbol }`. */
const useEllipsis = (ellipsis) => {
  const config = typeof ellipsis === 'object' && ellipsis !== null ? ellipsis : {}
  const enabled = Boolean(ellipsis)
  const rows = config.rows ?? 1
  const [expanded, setExpanded] = useState(false)

  const clampStyle =
    !enabled || expanded
      ? undefined
      : rows > 1
        ? {
            display: '-webkit-box',
            WebkitLineClamp: rows,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }
        : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

  const expandTrigger =
    enabled && config.expandable && !expanded ? (
      <button
        type="button"
        className="ml-1 text-[var(--primary-color)] underline"
        onClick={() => setExpanded(true)}
      >
        {config.symbol || 'plus'}
      </button>
    ) : null

  return { clampStyle, expandTrigger, title: enabled && config.tooltip ? undefined : config.title }
}

const Title = ({
  level = 1,
  className,
  style,
  children,
  ellipsis,
  type,
  disabled,
  strong,
  italic,
  underline,
  delete: del,
  mark,
  code,
  ...rest
}) => {
  const Tag = `h${Math.min(Math.max(Number(level) || 1, 1), 5)}`
  const { clampStyle, expandTrigger } = useEllipsis(ellipsis)

  return (
    <Tag
      className={cn(
        'ui-typography m-0 font-heading text-[var(--text-heading)]',
        LEVEL_CLASSES[level] || LEVEL_CLASSES[1],
        sharedClasses({ type, disabled, strong, italic, underline, delete: del, mark, code }),
        className
      )}
      style={{ ...clampStyle, ...style }}
      {...rest}
    >
      {children}
      {expandTrigger}
    </Tag>
  )
}

const Text = ({
  className,
  style,
  children,
  ellipsis,
  type,
  disabled,
  strong,
  italic,
  underline,
  delete: del,
  mark,
  code,
  keyboard,
  ...rest
}) => {
  const { clampStyle, expandTrigger } = useEllipsis(ellipsis)

  return (
    <span
      className={cn(
        'ui-typography',
        keyboard &&
          'rounded-[var(--radius-xs)] border border-[var(--border-color)] bg-[var(--gray-100)] px-1 font-mono',
        sharedClasses({ type, disabled, strong, italic, underline, delete: del, mark, code }),
        className
      )}
      style={{ ...clampStyle, ...style }}
      {...rest}
    >
      {children}
      {expandTrigger}
    </span>
  )
}

const Paragraph = ({
  className,
  style,
  children,
  ellipsis,
  type,
  disabled,
  strong,
  italic,
  underline,
  delete: del,
  mark,
  code,
  ...rest
}) => {
  const { clampStyle, expandTrigger } = useEllipsis(ellipsis)

  return (
    <p
      className={cn(
        'ui-typography mb-3 last:mb-0',
        sharedClasses({ type, disabled, strong, italic, underline, delete: del, mark, code }),
        className
      )}
      style={{ ...clampStyle, ...style }}
      {...rest}
    >
      {children}
      {expandTrigger}
    </p>
  )
}

const Link = ({ className, children, href, target, disabled, type, ...rest }) => (
  <a
    href={disabled ? undefined : href}
    target={target}
    rel={target === '_blank' ? 'noreferrer' : undefined}
    aria-disabled={disabled || undefined}
    className={cn(
      'ui-typography text-[var(--primary-color)] underline-offset-2 hover:underline',
      disabled && 'pointer-events-none text-[var(--gray-500)]',
      type && TYPE_CLASSES[type],
      className
    )}
    {...rest}
  >
    {children}
  </a>
)

const Typography = ({ className, children, ...rest }) => (
  <article className={cn('ui-typography', className)} {...rest}>
    {children}
  </article>
)

Typography.Title = Title
Typography.Text = Text
Typography.Paragraph = Paragraph
Typography.Link = Link

export { Link, Paragraph, Text, Title }
export default Typography
